import React, {
    useContext, useCallback, useEffect, useState, useMemo
} from 'react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { wizardProps, WizardContext } from 'wmic-pe-portals-custom-wizard-react';
import { WMICWizardChangeOrRenewalPage, useDocumentTitle, useWizardModals } from 'wmic-pe-portals-wizard-components-ui';
import { ViewModelServiceContext, ViewModelForm, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { CONSTANTS, HOConstants, WMICUserAccessUtil, WMICLogger, MODAL_CONSTANTS } from 'wmic-pe-portals-utils-js';
import { WMICHOAdditionalInterests, WMICHOWatercraftDetails, DwellingDetailView, WatercraftDetailView } from 'wmic-pe-capability-gateway-common-ho-react';
import WMICHORiskUtil from 'wmic-pe-capability-gateway-common-ho-react/utils/WMICHORiskUtil';
import { WMICScrollToError } from 'wmic-pe-components-platform-react';

import { messages as policyRenewalCommonMessages } from 'wmic-pe-capability-gateway-policyrenewal-common-react';
import * as WMICHOYourHomeUtil from 'wmic-pe-capability-gateway-quoteandbind-ho-react/util/WMICHOYourHomeUtil'
import defaultRisk from 'wmic-pe-capability-gateway-quoteandbind-ho-react/util/defaultRisk.json5';
import { messages as commonMessages } from 'wmic-pe-capability-gateway-policychange-common-react';
import metadata from './WMICHOPolicyRenewalRisksPage.metadata.json5';
import messages from './WMICHOPolicyRenewalRisksPage.messages';

const DWELLINGS_PATH = 'lobData.homeowners.coverables.dwellings';
const WATERCRAFT_PATH = 'lobData.homeowners.coverables.watercrafts';
const YOURHOMEMAP = `yourHome.${CONSTANTS.METADATAMAP}`;
const RATINGMAP = `rating.${CONSTANTS.METADATAMAP}`;
const ADLINTMAP = `additionalInterests.${CONSTANTS.METADATAMAP}`;

function recalculatePrimaryDwelling(renewal, dwelling) {
    const dwellings = _.get(renewal, 'lobData.homeowners.coverables.dwellings');
    const isPrimary = _.get(dwelling, 'yourHome.primaryDwelling_EXT');

    dwelling && isPrimary && dwellings.forEach((item) => {
        if(!_.isEqual(item, dwelling)) {
            _.set(item, 'yourHome.primaryDwelling_EXT', false);
        }
    })
}

function WMICHOPolicyRenewalRisksPage({
    wizardData: renewalVM,
    updateWizardData
}) {

    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const { RenewalService } = useDependencies('RenewalService');
    const {
        registerInitialComponentValidation,
        onValidate,
        isComponentValid,
        initialValidation,
    } = useValidation('WMICHOPolicyRenewalRisksPage');
    const { authHeader, authUserData } = useAuthentication();
    const { setWizardLoading } = useWizardModals();
    const { showConfirm } = useWizardModals();
    const { maintainFurtherStepsVisitedSubmitted, setIsCustomStepsInvalidated } = useContext(WizardContext);

    useDocumentTitle(translator(messages.risksTitle), renewalVM);

    // PAGE STATE
    const [isPageInitialized, setPageInitialized] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [scrollToError, setScrollToError] = useState()
    const [isReadOnlyUser, setReadOnlyUser] = useState(true);
    // eslint-disable-next-line no-warning-comments

    // initialise the data for the page
    useEffect(() => {
        registerInitialComponentValidation(() => WMICHOYourHomeUtil.presentDwellingRiskList(renewalVM));
        setPageInitialized(true);
        // The above action only need to run once when the page is mounted
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canEditRenewal = useMemo(() => WMICUserAccessUtil.canEditRenewal(authUserData.permission_Ext), [authUserData.permission_Ext]);

    const toCreateDwelling = useCallback( async () => {
        const skeleton = await RenewalService.getSkeletonStructure(
            renewalVM.value,
            authHeader
        );

        const _dwellingRiskViewVM = viewModelService.create(
            defaultRisk,
            'pc',
            'wmic.edge.ca.capabilities.policyjob.lob.homeownersHOE.coverables.dto.dwelling.DwellingDTO_WMIC',
            renewalVM.aspects.context()
        );
        // copy in skeletons for the Radiant data meta maps
        _.set(_dwellingRiskViewVM, YOURHOMEMAP, _.get(skeleton, `${DWELLINGS_PATH}[0].${YOURHOMEMAP}`));
        _.set(_dwellingRiskViewVM, RATINGMAP, _.get(skeleton, `${DWELLINGS_PATH}[0].${RATINGMAP}`));
        _.set(_dwellingRiskViewVM, ADLINTMAP, _.get(skeleton, `${DWELLINGS_PATH}[0].${ADLINTMAP}`));

        if (!WMICHOYourHomeUtil.presentDwellingRiskList(renewalVM)) {
            _.set(_dwellingRiskViewVM, 'yourHome.primaryDwelling_EXT.value', true);
        }

        _dwellingRiskViewVM.value.isUnderEditing = true;
        _dwellingRiskViewVM.value.isAddingLocation = false;
        _dwellingRiskViewVM.value.isAddingAdditionalInterest = false;
        _dwellingRiskViewVM.value.isAddingPetOnPremise = false;

        renewalVM.lobData.homeowners.coverables.dwellings.value.push(_dwellingRiskViewVM.value);
        return _dwellingRiskViewVM;
    }, [RenewalService, authHeader, renewalVM, viewModelService]);

    // PAGE HELPER FUNCTIONS
    // ADD NEW RISKx

    const toCreateDwellingWithModal = useCallback(() => showConfirm({
        title: policyRenewalCommonMessages.revalidationRequiredTitle,
        message: policyRenewalCommonMessages.revalidationRequiredBody,
        status: MODAL_CONSTANTS.STATUS.WARNING,
        icon: MODAL_CONSTANTS.ICON.ERROR,
        confirmButtonText: commonMessages.yes,
        cancelButtonText: commonMessages.cancelModel,
    }).then( (results) => {
        if (
            results === CONSTANTS.MODAL_RESULT.CANCEL ||
            results === CONSTANTS.MODAL_RESULT.CLOSE
        ) {
            return false;
        }
        if (maintainFurtherStepsVisitedSubmitted.flag) {
            maintainFurtherStepsVisitedSubmitted.flag = false;
            setIsCustomStepsInvalidated(true);
            updateWizardData(renewalVM);
        }
        return toCreateDwelling();
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [maintainFurtherStepsVisitedSubmitted, setIsCustomStepsInvalidated, renewalVM, showConfirm, toCreateDwelling])

    const saveRenewal = useCallback((newRenewalVM, onError=_.noop) => {
        setWizardLoading(true, translator(messages.savingRisk));

        return RenewalService.saveWithNoValidationRuleCheck(
            newRenewalVM,
            authHeader
        ).then((result) => {
            _.extend(newRenewalVM, result);
            renewalVM.value = newRenewalVM;
            updateWizardData(renewalVM);
            return true;
        }).catch((error) => {
            WMICLogger.error('Save risk failed', error);
            onError();
            return false;
        }).finally(() => {
            setWizardLoading(false);
        });
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [RenewalService, authHeader, setWizardLoading, renewalVM, translator]);

    const onDeleteDwelling = useCallback((item, index) => showConfirm({
            title: messages.removeRiskTitle,
            message: messages.removeRiskMessage,
        })
            .then((response) => {
                if (response === CONSTANTS.MODAL_RESULT.CONFIRM) {
                    const dwellings = _.get(renewalVM.value, DWELLINGS_PATH);

                    if (item && (
                        (item.yourHome && item.yourHome.residenceType === HOConstants.residenceTypeLaneway)
                         || (item.rating && (item.rating.hoPolicyType === HOConstants.revenuePropertyPolicyType
                             || item.rating.hoPolicyType === HOConstants.homeownerPolicyType)))) {
                        const associatedDwellingDeleted = _.find(renewalVM.value.lobData.homeowners.coverables.dwellings,
                            (dwelling) => dwelling.associatedDwellingPublicID === item.publicID);
                        if (associatedDwellingDeleted) {
                            associatedDwellingDeleted.associatedDwellingPublicID = undefined;
                        }
                    }
                    dwellings.splice(index, 1);
                    _.set(renewalVM.value, DWELLINGS_PATH, dwellings);
                    return saveRenewal(renewalVM.value);
                }
                return false;
            }), [saveRenewal, showConfirm, renewalVM]);

    const onSaveDwelling = useCallback((dwellingVM, index) => {
        const dwellingPath = `${DWELLINGS_PATH}.${index}`;
        recalculatePrimaryDwelling(renewalVM.value, dwellingVM.value);
        _.set(renewalVM.value, `${dwellingPath}.value.isUnderEditing`, false);
        return saveRenewal(renewalVM.value);
    }, [saveRenewal, renewalVM]);

    const toCreateWatercraft = useCallback(() => {
        const _watercraftRisk = {
            watercraftType: HOConstants.boatEquipmentTrailer,
            watercraftComponents: [],
            additionalInterests: []
        };
        const _watercraftRiskViewVM = viewModelService.create(
            _watercraftRisk,
            'pc',
            'wmic.edge.ca.capabilities.policyjob.lob.homeownersHOE.coverables.dto.watercraft.WatercraftRiskDTO_WMIC',
            renewalVM.aspects.context()
        );
        // _.set(_watercraftRiskviewVM, ADLINTMAP, _.get(skeletons, `${DWELLINGS_PATH}[0].${ADLINTMAP}`)); todo: needed when we fix the POC
        _.set(_watercraftRiskViewVM, ADLINTMAP, null);
        _watercraftRiskViewVM.value.isUnderEditing = true;
        _watercraftRiskViewVM.value.isAddingAdditionalInterest = false;
        _watercraftRiskViewVM.value.isAddingBoatMotor = false;

        renewalVM.lobData.homeowners.coverables.watercrafts.value.push(_watercraftRiskViewVM.value);
        return _watercraftRiskViewVM;
    }, [renewalVM.aspects, renewalVM.lobData.homeowners.coverables.watercrafts.value, viewModelService]);

    const toCreateWatercraftWithModal = useCallback(() => showConfirm({
        title: policyRenewalCommonMessages.revalidationRequiredTitle,
        message: policyRenewalCommonMessages.revalidationRequiredBody,
        status: MODAL_CONSTANTS.STATUS.WARNING,
        icon: MODAL_CONSTANTS.ICON.ERROR,
        confirmButtonText: commonMessages.yes,
        cancelButtonText: commonMessages.cancelModel,
    }).then( (results) => {
        if (
            results === CONSTANTS.MODAL_RESULT.CANCEL ||
            results === CONSTANTS.MODAL_RESULT.CLOSE
        ) {
            return false;
        }
        if (maintainFurtherStepsVisitedSubmitted.flag) {
            maintainFurtherStepsVisitedSubmitted.flag = false;
            setIsCustomStepsInvalidated(true);
            updateWizardData(renewalVM);
        }
        return toCreateWatercraft();
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [maintainFurtherStepsVisitedSubmitted, setIsCustomStepsInvalidated, renewalVM, showConfirm, toCreateWatercraft])

    const onDeleteWatercraft = useCallback((item, index) => showConfirm({
            title: messages.removeRiskTitle,
            message: messages.removeRiskMessage,
        })
            .then((response) => {
                if (response === CONSTANTS.MODAL_RESULT.CONFIRM) {
                    const watercrafts = _.get(renewalVM.value, WATERCRAFT_PATH);
                    watercrafts.splice(index, 1);
                    return saveRenewal(renewalVM.value);
                }
                return false;
            }), [saveRenewal, showConfirm, renewalVM]);

    const onSaveWatercraft = useCallback((watercraftVM, index) => {
        const watercraftPath = `${WATERCRAFT_PATH}.${index}`;
        _.set(renewalVM.value, `${watercraftPath}.value.isUnderEditing`, false);
        return saveRenewal(renewalVM.value);
    }, [saveRenewal, renewalVM]);

    const onEditDwelling = () => {
        _.set(renewalVM, 'isEditingPage.value', true);
        updateWizardData(renewalVM);
    }

    const onCancelDwelling = () => {
        _.set(renewalVM, 'isEditingPage.value', false);
        updateWizardData(renewalVM);
    }

    const onNext = useCallback(async () => {
        if (!isComponentValid || !WMICHOYourHomeUtil.presentDwellingRiskList(renewalVM)) {
            setShowErrors(true);
            setScrollToError(Date.now())
            return false;
        }

        try {
            setWizardLoading(true);

            const newRenewal = _.cloneDeep(renewalVM.value);
            renewalVM.value = await RenewalService.saveWithNoValidationRuleCheck(
                newRenewal,
                authHeader
            );
            updateWizardData(renewalVM);

            return renewalVM;
        } finally {
            setWizardLoading(false);
        }
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [RenewalService, authHeader, isComponentValid, renewalVM, setWizardLoading]);

    const overrideProps = {
        inlineNotification: {
            visible: WMICHOYourHomeUtil.presentWatercraftRiskList(renewalVM) && !WMICHOYourHomeUtil.presentDwellingRiskList(renewalVM)
        },
        trailerMonetaryValue: {
            parent: renewalVM.trailer
        },

        // DWELLING
        dwellingRequiredText: {
            visible: !WMICHOYourHomeUtil.presentDwellingRiskList(renewalVM),
            message: translator(messages.dwellingRequired)
        },
        dwellingListView: {
            clickable: true,
            readOnly: !canEditRenewal,
            VMData: [
                {
                    headerText: translator(messages.dwellingHeader),
                    path: 'yourHome.dwellingUsage',
                },
                {
                    headerText: translator(messages.riskHeader),
                    path: 'yourHome.dwellingNumber_EXT',
                    getData: (item, path) => WMICHOYourHomeUtil.renderDwellingRiskNumberCell(item, path, renewalVM)
                },
                {
                    headerText: translator(messages.addressHeader),
                    path: 'yourHome.locationAddress.displayName',
                    getData: (item) => WMICHOYourHomeUtil.renderDwellingAddressCell(item, translator)
                },
                {
                    headerText: translator(messages.riskTypeHeader),
                    path: 'rating.hoPolicyType'
                }
            ],
            detailViewComponent: DwellingDetailView,
            onValidate,
            toCreate: toCreateDwellingWithModal,
            toUndoCreate: () => {
                const dwellings = _.get(renewalVM.value, DWELLINGS_PATH);
                dwellings.splice(dwellings.length-1, 1);
                _.set(renewalVM.value, DWELLINGS_PATH, dwellings);
                updateWizardData(renewalVM);
            },
            onSave: onSaveDwelling,
            onDelete: onDeleteDwelling,
            toEdit: onEditDwelling,
            onCancel: onCancelDwelling,
            showCancelModal: true
        },
        watercraftListView: {
            clickable: true,
            readOnly: !canEditRenewal,
            startOpen: false,
            VMData: [
                {
                    headerText: translator(messages.riskHeader),
                    path: 'riskNumber'
                },
                {
                    headerText: translator(messages.riskDescription),
                    path: 'description',
                    getData: WMICHORiskUtil.getWaterCraftDescription
                },
                {
                    headerText: translator(messages.riskTypeHeader),
                    path: 'watercraftType'
                },
            ],
            detailViewComponent: WatercraftDetailView,
            onValidate,
            toCreate: toCreateWatercraftWithModal,
            toUndoCreate: () => {
                const watercrafts = _.get(renewalVM.value, WATERCRAFT_PATH);
                watercrafts.splice(watercrafts.length-1, 1);
                _.set(renewalVM.value, WATERCRAFT_PATH, watercrafts);
                updateWizardData(renewalVM);
            },
            onSave: onSaveWatercraft,
            onDelete: onDeleteWatercraft,
            showCancelModal: true
        }
    };

    const resolvers = {
        resolveCallbackMap: {},
        resolveComponentMap: {
            WMICHOAdditionalInterests,
            WMICHOWatercraftDetails
        }
    };

    if (!isPageInitialized) {
        return null;
    }

    return (
        <WMICWizardChangeOrRenewalPage
            onNext={onNext}
            cancelLabel={translator(commonMessages.saveAndExit)}
            isSkipping={initialValidation}
        >
            <WMICScrollToError counter={scrollToError}/>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={renewalVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                showErrors={showErrors}
            />
        </WMICWizardChangeOrRenewalPage>
    );
}

WMICHOPolicyRenewalRisksPage.propTypes = wizardProps;

export default withViewModelService(WMICHOPolicyRenewalRisksPage);
